import { combineLatest, merge, Observable, of, timer } from 'rxjs'
import {
  buffer,
  bufferCount,
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, OnInit } from '@angular/core'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { Store } from '@ngrx/store'
import { BulkAction, BulkActionEvent } from 'src/app/shared/interfaces'
import { NotificationService } from 'src/app/core/notification.service'
import { Booking } from '../../booking/booking'
import { UntilDestroy } from '@ngneat/until-destroy'
import { bookingActions } from '../../booking/booking.actions'
import { bookingSelectors } from '../../booking/booking.selectors'
import { courseSelectors } from '../course.selectors'
import { Course } from '../course'
import { courseActions } from '../course.actions'
import { AccessRights } from '../../role/user-role/user-role'
import { TranslateService } from '@ngx-translate/core'
import { ActivatedRoute, Router } from '@angular/router'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { Actions, ofType } from '@ngrx/effects'
import { DeleteBookingsModalComponent } from '../../booking/delete-bookings-modal/delete-bookings-modal.component'

@UntilDestroy()
@Component({
  selector: 'course-booking-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CourseBookingTabComponent implements OnInit {
  public accessRights = AccessRights
  public course$: Observable<Course>
  public courseId$: Observable<number>
  public isLoading$: Observable<boolean>
  public isLoadingBooking$: Observable<boolean>
  public bookings$: Observable<Booking[]>

  public BulkAction = BulkAction
  private didFetchCourse = false
  public isInModal: boolean = false

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private activeRoute: ActivatedRoute,
    public router: Router,
    private bsModalRef: BsModalRef,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.isLoadingBooking$ = this.store.select(bookingSelectors.selectIsLoading)

    this.isInModal = this.activeRoute.outlet === 'modal'

    this.courseId$ = this.courseId$ = this.activeRoute.params.pipe(
      map((params) =>
        params.hasOwnProperty('courseId') ? Number(params['courseId']) : null
      ),
      filter((id) => Boolean(id)),
      map((id) => +id),
      tap((courseId) => {
        if (!this.didFetchCourse) {
          this.store.dispatch(
            courseActions.fetchOneById({
              id: courseId,
              params: RequestQueryBuilder.create({
                join: [{ field: 'product' }, { field: 'product.translations' }]
              })
            })
          )

          this.store.dispatch(
            bookingActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { courseId },
                join: [
                  { field: 'tags' },
                  { field: 'product' },
                  { field: 'product.translations' },
                  { field: 'product.location' },
                  { field: 'linkedProduct' },
                  { field: 'linkedProduct.translations' },
                  { field: 'supervisorBookings' },
                  { field: 'participations' }
                ]
              })
            })
          )
          this.didFetchCourse = true
        }
      })
    )

    this.course$ = this.courseId$.pipe(
      switchMap((courseId) =>
        this.store
          .select(courseSelectors.selectEntities)
          .pipe(map((entities) => entities[courseId]))
      )
    )

    this.bookings$ = combineLatest([
      this.store.select(bookingSelectors.selectAll),
      this.course$
    ]).pipe(
      map(([bookings, course]) => {
        if (!bookings?.length || !course?.id) {
          return []
        }

        return bookings
          .filter((b) => b.courseId === course.id)
          .sort((a, b) => (a?.startDate > b?.startDate ? -1 : 1))
      })
    )
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent, type: string) {
    this.course$.pipe(take(1)).subscribe((course) => {
      if (!course) {
        this.notificationService.emit('alerts.error', 'error')
        return
      }

      switch (bulkActionEvent.action) {
        case BulkAction.DETACH:
          if (bulkActionEvent.items.length > 0) {
            this.actions$
              .pipe(
                ofType(bookingActions.upsertMany, bookingActions.upsertOne),
                bufferCount(bulkActionEvent?.items?.length),
                timeout(5000),
                take(1),
                catchError(() => of(null))
              )
              .subscribe((actions) => {
                if (actions?.length > 0) {
                  this.notificationService.emit('alerts.success', 'success')
                }
              })
          }
          for (const booking of bulkActionEvent.items) {
            this.store.dispatch(
              bookingActions.updateOneById({
                id: booking.id,
                params: {
                  courseId: null,
                  serieId: null,
                  startDate: booking.startDate,
                  endDate: booking.endDate
                }
              })
            )
          }

          break
        case BulkAction.DELETE:
          const modalRef = this.modalService.show(
            DeleteBookingsModalComponent,
            {
              initialState: {
                bookingIds: bulkActionEvent.items.map((item) =>
                  parseInt(item.id)
                )
              }
            }
          )

          this.notificationService.emit(
            'alerts.success',
            this.translate.instant('booking.delete.success')
          )
          break
      }
    })
  }

  public onClickedRow(booking: Booking) {
    if (booking) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/bookings/${booking.id}`])
      )

      window.open(url, '_blank')
    }
  }

  public onOpenCreateBooking() {
    this.course$.pipe(take(1)).subscribe((course) => {
      if (course) {
        if (this.isInModal) {
          this.bsModalRef.hide()
        }

        this.router.navigate(['/courses', course.id, 'bookings', 'create'])
      }
    })
  }
}
